import { isEqual } from 'lodash';
import {
	listItemKey,
	type StoryEditElementOwner,
	type StoryEditListAddress,
	type StoryEditListName,
	type StoryEditTarget,
	type StoryListChange,
	type StoryListOperation,
} from 'common/story-edit-operations/types';
import { toJsonValue, type PropertyChangeDocument } from 'common/story-edit-operations/property-patch';

// Ordered collections (steps, cards of a step, root and child blocks) change by ID-anchored list
// operations. The write is replayed on a simulated copy of the "before" collections, so each
// operation is valid at the moment it applies. See docs/adr/0003-id-anchored-list-operations.md.

type Model = Record<string, unknown>;

const isRecord = (value: unknown): value is Model =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

/** The document a collection belongs to. */
export type Container =
	| { kind: 'data'; list: 'steps' | 'elements' }
	| { kind: 'step'; stepId: string }
	| { kind: 'card'; cardId: string }
	| { kind: 'block'; blockId: string };

function listOf(container: Container): StoryEditListName {
	if (container.kind === 'data') return container.list;
	if (container.kind === 'step') return 'cards';
	if (container.kind === 'card') return 'elements';
	return 'children';
}

function collectionId(container: Container): string {
	if (container.kind === 'data') return `data/${container.list}`;
	if (container.kind === 'step') return `step:${container.stepId}/cards`;
	if (container.kind === 'card') return `card:${container.cardId}/elements`;
	return `block:${container.blockId}/children`;
}

/** The item owning a collection; top-level `data` lists have none. */
function containerKey(container: Container): string | undefined {
	if (container.kind === 'step') return container.stepId;
	if (container.kind === 'card') return container.cardId;
	if (container.kind === 'block') return container.blockId;
	return undefined;
}

/** Collections of building blocks: card roots, global roots and block children. */
const holdsBlocks = (container: Container) =>
	container.kind === 'card' ||
	container.kind === 'block' ||
	(container.kind === 'data' && container.list === 'elements');

/** Steps, cards and blocks move between collections by `_id`; references and symbol links never do. */
const isMovable = (key: string) => !key.startsWith('$');

/** The collection an item carries itself: a step's cards, a card's root blocks, a block's children. */
function nestedContainer(parent: Container, key: string, item: Model): [string, Container] | undefined {
	if (parent.kind === 'data' && parent.list === 'steps') return ['cards', { kind: 'step', stepId: key }];
	if (parent.kind === 'step') return ['elements', { kind: 'card', cardId: key }];
	if (Array.isArray(item.children)) return ['children', { kind: 'block', blockId: key }];
	return undefined;
}

/** Where collections and movable items are: shared by both indexed sides and the simulation. */
class Placement {
	readonly containers = new Map<string, Container>(); // collection ID → container

	readonly parentOf = new Map<string, string>(); // movable key → collection ID

	readonly masterRoots = new Map<string, string>(); // master root block ID → symbol ID

	ownerOf(blockId: string): StoryEditElementOwner {
		const symbolId = this.masterRoots.get(blockId);
		if (symbolId) return { kind: 'symbol-master', symbolId };
		const parent = this.parentOf.get(blockId);
		const container = parent ? this.containers.get(parent) : undefined;
		if (container?.kind === 'card') return { kind: 'card', cardId: container.cardId };
		if (container?.kind === 'data') return { kind: 'story' };
		if (container?.kind === 'block') return this.ownerOf(container.blockId);
		throw new Error(`Building block ${blockId} has no owner`);
	}

	targetOf(container: Container): StoryEditTarget {
		if (container.kind === 'data') return { kind: 'data' };
		if (container.kind === 'step') return { kind: 'step', stepId: container.stepId };
		if (container.kind === 'card') return { kind: 'card', cardId: container.cardId };
		return { kind: 'element', owner: this.ownerOf(container.blockId), elementId: container.blockId };
	}

	addressOf(collection: string): StoryEditListAddress {
		const container = this.containers.get(collection)!;
		return { target: this.targetOf(container), list: listOf(container) };
	}
}

/**
 * The compared collections of one side of a write, parents before children. `previous` (the
 * "before" side) keeps a block whose children turned from text into a list out of list operations:
 * that list is replaced as a property instead.
 */
export class StorySide extends Placement {
	readonly collections = new Map<string, unknown[]>(); // collection ID → items

	readonly items = new Map<string, Model>(); // movable key → item

	/** A key seen twice, or an item without a key: list operations cannot address this side. */
	isAmbiguous = false;

	constructor(private readonly previous?: StorySide) {
		super();
	}

	addCollection(container: Container, items: unknown) {
		if (!Array.isArray(items)) return;
		const id = collectionId(container);
		this.collections.set(id, items);
		this.containers.set(id, container);
		for (const item of items) {
			const key = listItemKey(item);
			if (!key) this.isAmbiguous = true;
			if (!key || !isMovable(key) || !isRecord(item)) continue;
			this.addItem(key, item);
			this.parentOf.set(key, id);
			if (holdsBlocks(container)) this.addChildren(key, item);
		}
	}

	addMasterRoot(symbolId: string, master: unknown) {
		const key = listItemKey(master);
		if (!key || !isMovable(key) || !isRecord(master)) return;
		this.addItem(key, master);
		this.masterRoots.set(key, symbolId);
		this.addChildren(key, master);
	}

	private addItem(key: string, item: Model) {
		if (this.items.has(key)) this.isAmbiguous = true;
		this.items.set(key, item);
	}

	private addChildren(key: string, block: Model) {
		const previous = this.previous?.items.get(key);
		const wasText = previous && !Array.isArray(previous.children);
		if (!wasText) this.addCollection({ kind: 'block', blockId: key }, block.children);
	}
}

/**
 * List operations of one write, in replay order: inserts and moves collection by collection
 * (parents first, so a new container exists before items move into it), then removals.
 */
export function collectionChanges(before: StorySide, after: StorySide): StoryListChange[] {
	const simulation = new Simulation(before);
	const changes: StoryListChange[] = [];

	for (const [id, items] of after.collections) {
		const container = after.containers.get(id)!;
		const operations = simulation.reorder(id, container, items);
		if (operations.length) changes.push(simulation.change(id, operations));
	}

	// Entries the write deleted, unless a deleted container takes them away with it.
	for (const [id, keys] of simulation.lists) {
		if (simulation.isInsideDeleted(id, after)) continue;
		const target = new Set((after.collections.get(id) ?? []).map(item => listItemKey(item)));
		const removed = keys.filter(key => !target.has(key));
		if (removed.length)
			changes.push(
				simulation.change(
					id,
					removed.map(key => ({ op: 'remove', key }))
				)
			);
	}
	return changes;
}

/** A mutable copy of the "before" collections, kept in step with every emitted operation. */
class Simulation extends Placement {
	readonly lists = new Map<string, string[]>(); // collection ID → item keys

	private readonly entries = new Map<string, unknown>(); // `${collection}|${key}` → reference or link value

	constructor(private readonly before: StorySide) {
		super();
		for (const [id, items] of before.collections) this.track(id, before.containers.get(id)!, items);
		before.parentOf.forEach((parent, key) => this.parentOf.set(key, parent));
		before.masterRoots.forEach((symbolId, key) => this.masterRoots.set(key, symbolId));
	}

	change(id: string, operations: StoryListOperation[]): StoryListChange {
		const { target, list } = this.addressOf(id);
		return { target, list, operations };
	}

	/** Operations that bring one collection into its "after" order. */
	reorder(id: string, container: Container, items: unknown[]): StoryListOperation[] {
		if (!this.lists.has(id)) this.track(id, container, []);
		const current = this.lists.get(id)!;
		const keys = items.map(item => listItemKey(item)!);
		const isUnchanged =
			keys.length === current.length &&
			keys.every((key, index) => key === current[index] && this.isSame(id, key, items[index]));
		if (isUnchanged) return [];

		const stable = this.stableKeys(id, current, keys, items);
		const operations: StoryListOperation[] = [];
		let previous: string | null = null;
		keys.forEach((key, index) => {
			if (!stable.has(key)) operations.push(...this.place(id, key, items[index], previous));
			previous = key;
		});
		return operations;
	}

	/** References and links are values: the same key with another value is another entry. */
	private isSame(id: string, key: string, item: unknown) {
		return isMovable(key) || isEqual(this.entries.get(`${id}|${key}`), item);
	}

	/** Entries already in the collection that keep their relative order: the longest such run stays. */
	private stableKeys(id: string, current: string[], keys: string[], items: unknown[]): Set<string> {
		const positions = new Map(current.map((key, index) => [key, index]));
		const present = keys.filter((key, index) => positions.has(key) && this.isSame(id, key, items[index]));
		const run = longestIncreasingRun(present.map(key => positions.get(key)!));
		return new Set(run.map(index => present[index]));
	}

	private place(id: string, key: string, item: unknown, after: string | null): StoryListOperation[] {
		// An existing step, card or block moves with its content, from wherever it is now.
		const source = isMovable(key) ? this.parentOf.get(key) : undefined;
		if (source) {
			const from = source === id ? undefined : this.addressOf(source);
			this.detach(source, key);
			this.attach(id, key, after);
			return [from ? { op: 'move', key, after, from } : { op: 'move', key, after }];
		}

		const operations: StoryListOperation[] = [];
		if (this.lists.get(id)!.includes(key)) {
			// A reference or link whose value changed.
			this.detach(id, key);
			operations.push({ op: 'remove', key });
		}
		// Detached plain JSON: delivery is asynchronous.
		operations.push({ op: 'insert', after, value: toJsonValue(this.inserted(id, key, item)) });
		this.attach(id, key, after);
		return operations;
	}

	/**
	 * A new item without the existing steps, cards or blocks inside it: those move in afterwards and
	 * keep their content. The collections it brings start out as inserted.
	 */
	private inserted(id: string, key: string, item: unknown): unknown {
		if (!isMovable(key) || !isRecord(item)) {
			this.entries.set(`${id}|${key}`, item);
			return item;
		}
		const nested = nestedContainer(this.containers.get(id)!, key, item);
		if (!nested) return item;
		const [member, container] = nested;
		const children = Array.isArray(item[member]) ? (item[member] as unknown[]) : [];
		const kept = children.filter(child => {
			const childKey = listItemKey(child);
			return !childKey || !isMovable(childKey) || !this.before.items.has(childKey);
		});
		const childId = collectionId(container);
		this.track(childId, container, kept);
		return {
			...item,
			[member]: kept.map(child => this.inserted(childId, listItemKey(child)!, child)),
		};
	}

	private track(id: string, container: Container, items: unknown[]) {
		const keys: string[] = [];
		for (const item of items) {
			const key = listItemKey(item);
			if (!key) continue;
			keys.push(key);
			if (isMovable(key)) this.parentOf.set(key, id);
			else this.entries.set(`${id}|${key}`, item);
		}
		this.lists.set(id, keys);
		this.containers.set(id, container);
	}

	private attach(id: string, key: string, after: string | null) {
		const list = this.lists.get(id)!;
		list.splice(after === null ? 0 : list.indexOf(after) + 1, 0, key);
		if (isMovable(key)) this.parentOf.set(key, id);
	}

	private detach(id: string, key: string) {
		const list = this.lists.get(id)!;
		list.splice(list.indexOf(key), 1);
	}

	/** A collection whose container, or an ancestor of it, no longer exists after the write. */
	isInsideDeleted(id: string, after: StorySide): boolean {
		const key = containerKey(this.containers.get(id)!);
		if (!key) return false;
		if (!after.items.has(key)) return true;
		const parent = this.parentOf.get(key);
		return parent ? this.isInsideDeleted(parent, after) : false;
	}
}

/** Indexes into `values` of one longest strictly increasing subsequence. */
function longestIncreasingRun(values: number[]): number[] {
	const tails: number[] = []; // per run length, the index of the smallest tail value
	const previous: number[] = values.map(() => -1);
	values.forEach((value, index) => {
		let low = 0;
		let high = tails.length;
		while (low < high) {
			const middle = Math.floor((low + high) / 2);
			if (values[tails[middle]] < value) low = middle + 1;
			else high = middle;
		}
		if (low > 0) previous[index] = tails[low - 1];
		tails[low] = index;
	});
	const run: number[] = [];
	for (let index = tails.length ? tails[tails.length - 1] : -1; index >= 0; index = previous[index]) {
		run.unshift(index);
	}
	return run;
}

/** Property documents of the steps, cards and blocks present on both sides. */
export function pairedItemDocuments(before: StorySide, after: StorySide): PropertyChangeDocument[] {
	const documents: PropertyChangeDocument[] = [];
	for (const [key, item] of after.items) {
		const previous = before.items.get(key);
		if (!previous || previous === item) continue;
		const parent = after.parentOf.get(key);
		const container = parent ? after.containers.get(parent) : undefined;
		if (container?.kind === 'data' && container.list === 'steps') {
			documents.push(memberDocument({ kind: 'step', stepId: key }, previous, item, 'cards'));
		} else if (container?.kind === 'step') {
			documents.push(memberDocument({ kind: 'card', cardId: key }, previous, item, 'elements'));
		} else {
			documents.push(...blockDocuments(after.ownerOf(key), key, previous, item));
		}
	}
	return documents;
}

/** An item's properties without the collection it holds. */
const memberDocument = (target: StoryEditTarget, before: Model, after: Model, collection: string) => ({
	target,
	before: { ...before, [collection]: undefined },
	after: { ...after, [collection]: undefined },
});

/** A block's properties. Text children are a property; a list of children changes by list operations. */
function blockDocuments(
	owner: StoryEditElementOwner,
	id: string,
	before: Model,
	after: Model
): PropertyChangeDocument[] {
	const target = { kind: 'element', owner, elementId: id } as const;
	const documents = [memberDocument(target, before, after, 'children')];
	if (!Array.isArray(before.children) || !Array.isArray(after.children)) {
		documents.push({ target, before: { children: before.children }, after: { children: after.children } });
	}
	return documents;
}
